import React, { useState } from 'react';
import { safeUrl } from "../../utils/safeUrl";
import { FiX, FiCheck, FiClock, FiExternalLink, FiMaximize2, FiMinimize2, FiAlertTriangle } from 'react-icons/fi';
import toast from 'react-hot-toast';

export const EXPANDED_HIDDEN_FIELDS = [
  'id', 'title', 'lastcollectedreward', 'nextreward', 'requestedreward', 'rejectreason',
  'type', 'status', 'collectedbonus', 'nextrewardview', 'updatedat', 'user_username',
  'userid', 'lastview', 'nextrewardpx', 'createdat', 'user_email',
];

export const baseUrl = process.env.REACT_APP_API_BASE_URL;

export const getEmbedUrl = (url) => {
  if (!url) return null;
  const strUrl = String(url);
  // YouTube
  const ytMatch = strUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Instagram
  const igMatch = strUrl.match(/(?:instagram\.com)\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
  if (igMatch) return `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed`;
  // TikTok
  const ttMatch = strUrl.match(/(?:tiktok\.com)\/@[^\/]+\/(?:video|photo)\/(\d+)/);
  if (ttMatch) return `https://www.tiktok.com/embed/v2/${ttMatch[1]}`;

  return null;
};


export const extractUsername = (url) => {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url.toLowerCase().trim());
    const path = u.pathname;
    
    if (u.hostname.includes('tiktok.com')) {
      const match = path.match(/\/@([a-z0-9_.-]+)/);
      if (match) return match[1];
    }
    
    if (u.hostname.includes('youtube.com')) {
      const match = path.match(/\/@([a-z0-9_.-]+)/);
      if (match) return match[1];
      const matchC = path.match(/\/(?:c|channel|user)\/([a-z0-9_.-]+)/);
      if (matchC) return matchC[1];
    }
    
    if (u.hostname.includes('instagram.com')) {
      const parts = path.split('/').filter(Boolean);
      if (parts.length > 0) {
        if (!['p', 'reel', 'tv', 'explore', 'stories'].includes(parts[0])) {
          return parts[0];
        }
      }
    }
  } catch (e) {
    return null;
  }
};

export const openProfilePopup = (rawProfileUrl) => {
  const profileUrl = safeUrl(rawProfileUrl);
  if (!profileUrl) return;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  // Responsive dimensions: Width ~45% of screen (max 1200, min 400). Height ~80% of screen (max 900, min 500)
  const width = Math.max(400, Math.min(1200, Math.round(screenWidth * 0.45)));
  const height = Math.max(500, Math.min(900, Math.round(screenHeight * 0.8)));

  // Calculate bottom right position
  const left = screenWidth - width - 10;
  const top = screenHeight - height - 50;
  
  const newWindow = window.open(
    profileUrl,
    "platformProfile",
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    import('react-hot-toast').then(({ default: toast }) => {
      toast.error("Popup was blocked by your browser. Please allow popups for this site.");
    });
  } else if (newWindow.focus) {
    newWindow.focus();
  }
};

export const getVerifiedUsername = (detail) => {
  if (!detail || !detail.type || !detail.profileVerificationData || !Array.isArray(detail.profileVerificationData)) return null;
  const platform = String(detail.type).toLowerCase();
  const urlKey = `${platform}ProfileUrl`;
  const vd = [...detail.profileVerificationData].reverse().find(e => e[urlKey]);
  if (vd && vd[urlKey]) {
    return extractUsername(vd[urlKey]);
  }
  return null;
};

export const getVerifiedProfileUrl = (detail) => {
  if (!detail || !detail.type || !detail.profileVerificationData || !Array.isArray(detail.profileVerificationData)) return null;
  const platform = String(detail.type).toLowerCase();
  const urlKey = `${platform}ProfileUrl`;
  const vd = [...detail.profileVerificationData].reverse().find(e => e[urlKey]);
  if (vd && vd[urlKey]) {
    return vd[urlKey];
  }
  return null;
};

export const ProfileMatchWarning = ({ detail }) => {
  if (!detail || !detail.url || !detail.type || !detail.profileVerificationData) return null;
  
  const platform = detail.type.toLowerCase();
  const submittedUrl = detail.url;
  
  const urlKey = `${platform}ProfileUrl`;
  const verificationData = [...detail.profileVerificationData].reverse().find(e => e[urlKey]);
  
  if (!verificationData) return null;
  
  const status = platform === 'youtube' ? (verificationData.status || '') : (verificationData[`${platform}Status`] || '');
  if (status.toLowerCase() !== 'approved') return null;
  
  const verifiedUrl = verificationData[urlKey];
  
  const submittedUsername = extractUsername(submittedUrl);
  const verifiedUsername = extractUsername(verifiedUrl);
  
  if (!verifiedUsername) return null; // We definitely need the verified username to compare against
  
  if (!submittedUsername) {
    return (
      <div className="mt-4 mb-2 flex items-start gap-3 bg-yellow-50/80 border border-yellow-200 p-4 rounded-xl">
        <FiAlertTriangle size={18} className="text-yellow-600 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-bold text-yellow-800 mb-1">
            Manual Verification Required
          </div>
          <div className="text-xs text-yellow-700 space-y-1">
            <div>The submitted URL format does not contain a username, so we cannot automatically verify if it belongs to <span className="font-semibold">@{verifiedUsername}</span>.</div>
          </div>
        </div>
      </div>
    );
  }
  
  if (submittedUsername === verifiedUsername) {
    return (
      <div className="mt-4 mb-2 flex items-start gap-2 bg-green-50 border border-green-100 p-3 rounded-lg">
        <FiCheck size={16} className="text-green-600 mt-0.5" />
        <div className="text-sm font-medium text-green-700">
          Content matches the verified profile.
        </div>
      </div>
    );
  }
  
  return (
    <div className="mt-4 mb-2 flex items-start gap-3 bg-red-50/50 border border-red-200 p-4 rounded-xl">
      <FiAlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
      <div>
        <div className="text-sm font-bold text-red-700 mb-1">
          This content does not belong to the creator's verified {platform.charAt(0).toUpperCase() + platform.slice(1)} account.
        </div>
        <div className="text-xs text-red-600 space-y-1">
          <div><span className="font-semibold">Verified Account:</span> @{verifiedUsername}</div>
          <div><span className="font-semibold">Submitted Account:</span> @{submittedUsername}</div>
        </div>
      </div>
    </div>
  );
};

export const REJECTION_REASONS = [
  {
    title: 'Content Not Px About',
    reason: 'The submitted video does not meet PX Creator Hub content guidelines.\nKindly ensure your content is related to PX Network and provides useful guidance to the community.',
  },
  {
    title: 'Content from Other Account',
    reason: 'Thank you for your submission.\nPlease note that PX Creator Hub approves content only from your verified account.\nThe submitted video appears to be uploaded from a different or unverified account, so it cannot be approved.\nKindly upload PX-related content using your registered and verified creator account.',
  },
  {
    title: 'Duplicate Content',
    reason: 'Thank you for your submission.\nThis content has already been submitted or approved previously.\nPlease upload new and original PX Network content to be eligible for approval.',
  },
  {
    title: 'Private or Unavailable Content',
    reason: 'Thank you for your submission.\nWe were unable to access the submitted content because it is private, deleted, or unavailable.\nPlease ensure your content is publicly accessible before submitting it for review.',
  },
  {
    title: 'Invalid Submission Link',
    reason: 'Thank you for your submission.\nThe submitted link is invalid or does not direct to the intended content.\nPlease verify the link and submit a valid public URL.',
  },
  {
    title: 'Insufficient PX Content',
    reason: 'Thank you for your submission.\nThe content contains only limited information about PX Network and does not provide enough value for approval.\nPlease create more detailed and informative PX-related content before resubmitting.',
  },
  {
    title: 'Misleading Information',
    reason: 'Thank you for your submission.\nThe content contains inaccurate or misleading information about PX Network.\nPlease review the official PX Network information and submit corrected content.',
  },
  {
    title: 'Creator Guidelines Not Met',
    reason: 'Thank you for your submission.\nThe submitted content does not meet the current PX Creator Hub content guidelines.\nPlease review the Creator Hub requirements and submit original, high-quality PX Network content for approval.',
  },
];

export const Spinner = ({ sm }) => (
  <div className={`border-2 border-current border-t-transparent rounded-full animate-spin ${sm ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
);

export const StatusBadge = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const cls = s === 'approved'
    ? 'bg-green-50 text-green-700'
    : (s === 'rejected' || s === 'reject')
      ? 'bg-red-50 text-red-600'
      : 'bg-amber-50 text-amber-600';
  const label = s === 'approved' ? 'Approved' : (s === 'rejected' || s === 'reject') ? 'Rejected' : 'Pending';
  return <span className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

export const Modal = ({ title, onClose, children, footer, size = 'md' }) => {
  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-3xl', xl: 'max-w-5xl' };
  const blockEnterOutsideButtons = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className={`bg-white rounded-xl shadow-xl w-full ${sizes[size]} flex flex-col max-h-[90vh]`}
        onClick={e => e.stopPropagation()}
        onKeyDown={blockEnterOutsideButtons}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
            <FiX size={16} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-6">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-xl flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const DetailRow = ({ label, value, mono, link }) => (
  <div>
    <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">{label}</div>
    {link && value ? (
      <a href={safeUrl(value)} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline break-all flex items-center gap-1">
        {String(value)} <FiExternalLink size={11} />
      </a>
    ) : (
      <div className={`text-sm text-gray-800 break-words ${mono ? 'font-mono text-xs' : ''}`}>
        {value === null || value === undefined || value === '' ? <span className="text-gray-300">—</span> : String(value)}
      </div>
    )}
  </div>
);

export const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const ApplicationModals = ({
  isDetailOpen, setIsDetailOpen,
  selectedDetail, setSelectedDetail, detailLoading,
  modal, setModal, modalUrl, setModalUrl, modalMessage, setModalMessage,
  selectedReasonTitle, setSelectedReasonTitle, handleStatusUpdate, actionLoading,
  historyModalOpen, setHistoryModalOpen, historyLoading, historyData, updateMilestoneStatus, milestoneActionLoading
}) => {
  return (
    <>
      {isDetailOpen && (
        <Modal
          title="Application Details"
          onClose={() => { setIsDetailOpen(false); setSelectedDetail(null); }}
          size="lg"
        >
          {detailLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : selectedDetail ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {Object.entries(selectedDetail)
                  .filter(([key]) => !key.includes('Verification') && key !== 'profileVerificationData')
                  .map(([k, v]) => (
                    <DetailRow
                      key={k}
                      label={k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                      value={v === null ? null : String(v)}
                      link={k.toLowerCase().includes('url') || k.toLowerCase().includes('link')}
                    />
                  ))}
              </div>

              {Array.isArray(selectedDetail.profileVerificationData) && selectedDetail.profileVerificationData.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 pt-2 border-t border-gray-100">
                    Profile Verification
                  </div>
                  <div className="space-y-3">
                    {([String(selectedDetail.type || '').toLowerCase()].filter(p => ['youtube', 'instagram', 'tiktok'].includes(p)).length > 0 
                      ? [String(selectedDetail.type || '').toLowerCase()].filter(p => ['youtube', 'instagram', 'tiktok'].includes(p)) 
                      : ['youtube', 'instagram', 'tiktok']
                    ).map(platform => {
                      const urlKey = `${platform}ProfileUrl`;
                      const vd = [...selectedDetail.profileVerificationData].reverse().find(e => e[urlKey]) || {};
                      const profileUrl = vd[urlKey];
                      const code = vd[`${platform}Code`];
                      const isCodeSent = vd[`is${platform.charAt(0).toUpperCase() + platform.slice(1)}CodeSend`];
                      const status = platform === 'youtube' ? (vd['status'] || 'notAdded') : (vd[`${platform}Status`] || 'notAdded');
                      if (!profileUrl) return null;
                      return (
                        <div key={platform} className="border border-gray-100 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold capitalize text-sm text-gray-700">{platform}</span>
                              <StatusBadge status={status} />
                            </div>
                            <button 
                              onClick={(e) => { e.stopPropagation(); openProfilePopup(profileUrl); }} 
                              className="text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition flex items-center gap-1.5"
                            >
                              View Profile <FiExternalLink size={11} />
                            </button>
                          </div>
                          <div className="mb-3 space-y-3">
                            <DetailRow label="Profile URL" value={profileUrl} link />
                            <DetailRow 
                              label="Username" 
                              value={extractUsername(profileUrl) ? `@${extractUsername(profileUrl)}` : 'Not Available'} 
                            />
                          </div>
                          {(code || isCodeSent !== undefined) && (
                            <div className="grid grid-cols-2 gap-3">
                              <DetailRow label="Verification Code" value={code || 'N/A'} mono />
                              <DetailRow label="Code Status" value={isCodeSent === 1 ? 'Sent' : 'Not Sent'} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <p className="text-sm">No details available.</p>
            </div>
          )}
        </Modal>
      )}

      {modal && (
        <Modal
          title={modal.type === 'approve' ? 'Approve Application' : 'Reject Application'}
          onClose={() => setModal(null)}
          footer={
            <>
              <button
                onClick={() => setModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={actionLoading}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-60 ${modal.type === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
              >
                {actionLoading && <Spinner sm />}
                {actionLoading ? 'Processing...' : modal.type === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </>
          }
        >
          {modal.type === 'approve' ? (
            <div className="space-y-4">
              {modalUrl && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Current Link</label>
                  <div className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm break-all">
                    <a href={safeUrl(modalUrl)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                      {modalUrl}
                    </a>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  {modalUrl ? 'Update Link (optional)' : 'Link (optional)'}
                </label>
                <input
                  value={modalUrl}
                  onChange={e => setModalUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-transparent"
                  placeholder="https://..."
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Quick reason (optional)</label>
                <select
                  value={selectedReasonTitle}
                  onChange={e => {
                    const title = e.target.value;
                    setSelectedReasonTitle(title);
                    const preset = REJECTION_REASONS.find(r => r.title === title);
                    setModalMessage(preset ? preset.reason : '');
                  }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-transparent bg-white"
                >
                  <option value="">-- Select a pre-written reason --</option>
                  {REJECTION_REASONS.map(r => (
                    <option key={r.title} value={r.title}>{r.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Rejection reason</label>
                <textarea
                  value={modalMessage}
                  onChange={e => { setModalMessage(e.target.value); setSelectedReasonTitle(''); }}
                  rows={6}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-transparent resize-none"
                  placeholder="Enter reason for rejection, or pick a quick reason above"
                />
              </div>
            </div>
          )}
        </Modal>
      )}

      {historyModalOpen && (
        <Modal
          title="Milestone History"
          onClose={() => setHistoryModalOpen(false)}
          size="xl"
        >
          {historyLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : historyData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                    <th className="px-4 py-3">Creator</th>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">URL</th>
                    <th className="px-4 py-3 text-right">Milestone</th>
                    <th className="px-4 py-3 text-right">Reward PX</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {historyData.map((h, idx) => {
                    const hId = h.id || h._id;
                    const hApproved = String(h.status || '').toLowerCase() === 'approved';
                    const hRejected = ['rejected', 'reject'].includes(String(h.status || '').toLowerCase());
                    return (
                      <tr key={idx} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-gray-700">{h.userName || h.user_name || h.username || '—'}</td>
                        <td className="px-4 py-3 max-w-[140px]">
                          <span className="block truncate text-gray-700">{h.title || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-medium bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                            {h.type || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {h.url ? (
                            <a href={safeUrl(h.url)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1 text-xs">
                              Link <FiExternalLink size={11} />
                            </a>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">{h.milestone || '—'}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-800">
                          {h.rewardPx || h.reward_px || '—'}
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={h.status} /></td>
                        <td className="px-4 py-3 text-xs text-gray-400">{formatDate(h.createdAt || h.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 justify-center">
                            {!hApproved && (
                              <button
                                onClick={() => updateMilestoneStatus(hId, 'approved')}
                                disabled={milestoneActionLoading === hId}
                                title="Approve Milestone"
                                className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition disabled:opacity-40"
                              >
                                {milestoneActionLoading === hId ? <Spinner sm /> : <FiCheck size={13} />}
                              </button>
                            )}
                            {!hRejected && (
                              <button
                                onClick={() => updateMilestoneStatus(hId, 'rejected')}
                                disabled={milestoneActionLoading === hId}
                                title="Reject Milestone"
                                className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition disabled:opacity-40"
                              >
                                {milestoneActionLoading === hId ? <Spinner sm /> : <FiX size={13} />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <FiClock size={28} className="text-gray-300 mb-2" />
              <p className="text-sm">No milestone history found.</p>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};

export const useApplicationActions = (onActionComplete) => {
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [modalUrl, setModalUrl] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [selectedReasonTitle, setSelectedReasonTitle] = useState('');
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [milestoneActionLoading, setMilestoneActionLoading] = useState(null);

  const openDetail = async (id) => {
    setIsDetailOpen(true);
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Not authenticated'); setDetailLoading(false); return; }
      const res = await fetch(`${baseUrl}/creator-hub/application/details/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok && result) {
        setSelectedDetail(result.data ?? result);
      } else {
        toast.error(result?.message || 'Failed to load details');
      }
    } catch {
      toast.error('Network error while fetching details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!modal) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Not authenticated'); setActionLoading(false); return; }
      const payload = modal.type === 'approve'
        ? { status: 'approved', url: modalUrl }
        : { status: 'rejected', message: modalMessage };
      const res = await fetch(`${baseUrl}/creator-hub/application/status/${modal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(result?.message || 'Status updated');
        setModal(null);
        if (onActionComplete) onActionComplete();
      } else {
        toast.error(result?.message || 'Failed to update status');
      }
    } catch {
      toast.error('Network error while updating status');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchHistory = async (creatorId) => {
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Not authenticated'); setHistoryLoading(false); return; }
      const res = await fetch(`${baseUrl}/creator-hub/application/milestone-history?creatorId=${creatorId}`, {
        headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
      });
      const result = await res.json();
      if (res.ok && result) {
        const data = result.data ?? result;
        setHistoryData(Array.isArray(data) ? data : []);
      } else {
        toast.error(result?.message || 'Failed to fetch history');
      }
    } catch {
      toast.error('Network error while fetching history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const updateMilestoneStatus = async (milestoneId, status) => {
    setMilestoneActionLoading(milestoneId);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Not authenticated'); setMilestoneActionLoading(null); return; }
      const res = await fetch(`${baseUrl}/creator-hub/application/milestone-history/status/${milestoneId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(result?.message || `Milestone ${status} successfully`);
        setHistoryData(prev => prev.map(item =>
          (item.id === milestoneId || item._id === milestoneId) ? { ...item, status } : item
        ));
        if (onActionComplete) onActionComplete();
      } else {
        toast.error(result?.message || 'Failed to update milestone status');
      }
    } catch {
      toast.error('Network error while updating milestone status');
    } finally {
      setMilestoneActionLoading(null);
    }
  };

  return {
    detailLoading, selectedDetail, setSelectedDetail, isDetailOpen, setIsDetailOpen,
    actionLoading, modal, setModal, modalUrl, setModalUrl, modalMessage, setModalMessage,
    selectedReasonTitle, setSelectedReasonTitle, historyModalOpen, setHistoryModalOpen,
    historyLoading, historyData, milestoneActionLoading,
    openDetail, handleStatusUpdate, fetchHistory, updateMilestoneStatus
  };
};

export const FullscreenPreviewModal = ({ url, embedUrl, onClose, verifiedUsername }) => {
  const [reloadKey, setReloadKey] = useState(0);

  const handleReload = (e) => {
    e.stopPropagation();
    setReloadKey(prev => prev + 1);
  };

  const displayUsername = verifiedUsername || extractUsername(url);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative w-full h-full max-w-6xl max-h-screen flex flex-col bg-gray-900 rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
          <div className="text-sm font-medium text-gray-200 flex items-center gap-2">
            Preview
            {displayUsername && (
              <>
                <span className="text-gray-500">|</span>
                <span className="text-blue-300">@{displayUsername}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReload}
              title="Reload preview to watch again"
              className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-white transition bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded"
            >
              <FiClock size={12} className="rotate-180" /> Reload
            </button>
            <a
              href={safeUrl(url)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-xs font-medium text-blue-400 hover:text-blue-300 transition"
            >
              Open Original <FiExternalLink size={12} />
            </a>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition"
              title="Close Fullscreen"
            >
              <FiMinimize2 size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 w-full h-full relative flex items-center justify-center bg-gray-950">
          <iframe
            key={`fullscreen-preview-${reloadKey}`}
            src={embedUrl}
            title="fullscreen-preview"
            className="w-full max-w-lg h-full border-none bg-white"
            sandbox="allow-scripts allow-same-origin allow-presentation"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

export const ContentPreview = ({ url, detail }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const embedUrl = getEmbedUrl(url);

  const verifiedUsername = detail ? getVerifiedUsername(detail) : null;

  const handleReload = (e) => {
    e.stopPropagation();
    setReloadKey(prev => prev + 1);
  };

  return (
    <div className="mt-8 w-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 pt-4 border-t border-gray-100 gap-3">
        <div className="text-sm font-semibold text-gray-700 tracking-wide">
          Content Preview
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {embedUrl && (
            <button
              onClick={handleReload}
              title="Reload preview to watch again"
              className="flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg transition border border-gray-200"
            >
              <FiClock size={14} className="rotate-180" /> Reload
            </button>
          )}
          <a
            href={safeUrl(url)}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition"
          >
            <FiExternalLink size={14} /> Open Original
          </a>
          {embedUrl && (
            <button
              onClick={(e) => { e.stopPropagation(); setIsFullscreen(true); }}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition"
            >
              <FiMaximize2 size={14} /> Full Screen
            </button>
          )}
        </div>
      </div>
      
      {embedUrl ? (
        <div className="w-full max-w-[300px] h-[500px] md:h-[300px] border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm relative">
          <iframe
            key={reloadKey}
            src={embedUrl}
            title="Content Preview"
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="w-full max-w-lg p-6 bg-gray-50 border border-gray-100 rounded-xl flex flex-col items-center justify-center text-center space-y-3">
          <FiExternalLink size={32} className="text-gray-300" />
          <div>
            <h4 className="text-sm font-semibold text-gray-700">Preview Unavailable</h4>
            <p className="text-xs text-gray-500 mt-1 max-w-sm">
              We cannot load an automatic preview for this link format (e.g. short links or profile links).
            </p>
          </div>
          <a
            href={safeUrl(url)}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition shadow-sm"
          >
            Open in New Tab
          </a>
        </div>
      )}

      {isFullscreen && embedUrl && (
        <FullscreenPreviewModal
          url={url}
          embedUrl={embedUrl}
          onClose={() => setIsFullscreen(false)}
          verifiedUsername={verifiedUsername}
        />
      )}
    </div>
  );
};
